import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import { getHighestVisitedStep } from "~/features/configurator/Linear/redux/selectors";
import { sendRailUpdate } from "~/features/configurator/Rail/api/sendRailConfiguratorUpdate";
import { sendRailStepChange } from "~/features/configurator/Rail/api/sendRailStepChange";
import { sendRailDataReset } from "~/features/configurator/Rail/api/sendRailStepReset";
import { sendRailResetConfigurator } from "~/features/configurator/Rail/api/sendRailResetConfigurator";
import { sendRailAddToCart } from "~/features/configurator/Rail/api/sendRailAddToCart";
import { RailSummaryData } from "~/features/configurator/Rail/v2/configuration/classic/summary/RailSummaryData";
import {
  RAIL_MIN_STEP_TO_BUY,
  RAIL_MIN_STEP_TO_SAVE,
} from "~/features/configurator/Rail/v2/configuration/classic/railConfiguratorConstants";
import { type RootState } from "~/app/store";
import { BASE_DESIGNATION_SEPARATOR, isGuidewayPieceSizeCsticName } from "~/features/configurator/Linear/constants";
import {
  type ConfiguratorPageDefinition,
  type StepHardcodedData,
} from "~/features/configurator/ConfiguratorPageDefinition";
import { isNonNull } from "~/common/utils/reducers";
import { RAIL_CONFIGURATOR_PRODUCT_CODE } from "~/domains/configurator/constants";
import { getMainKmat } from "~/features/configurator/Shaft/common/commonShaftHelpers";
import * as RailCstic from "~/features/configurator/Rail/v2/configuration/classic/RailsCstics";
import { SubstepType } from "~/features/configurator/Linear/dtos";
import { makeSendStepCompletedEvent, makeSummaryDataProvider } from "~/features/configurator/Shaft/shaftGaHelper";

import { HelperCstics } from "~/features/configurator/Rail/components/ConfigureRailSubstep/constants";
import type { LinearCstic } from "~/features/configurator/Linear/models";
import { csticHasValue, getCsticsValue } from "~/features/configurator/common/v2/helper";
import {
  getStep1SummaryData,
  getStep2SummaryData,
  getStep3SummaryData,
  getStep4SummaryData,
} from "./summary/RailSummaryDataProvider";
import {
  prepareSubStepsInfo,
  transformSummaryDataToV2,
  transformToSelectionData,
} from "~/features/configurator/Shaft/SummaryDataProvider";
import { CsticNamingHelper } from "~/features/configurator/Linear/v2/configuration/kllt/CsticNamingHelper";
import instance from "~/utils/i18n";

const RAIL_CONFIGURATOR_STEP_DATA: StepHardcodedData[] = [
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      let step1Data = getStep1SummaryData(summaryDataProvider) ?? [];

      const substeps = [SubstepType.Series, SubstepType.Size];
      step1Data = [...prepareSubStepsInfo(summaryDataProvider, substeps)!, ...step1Data];

      return {
        name: summaryDataProvider.getGaStepName(1),
        gaName: summaryDataProvider.getGaStepName(1),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step1Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = RAIL_CONFIGURATOR_STEP_DATA[0].prepareData(state, "en");
      const selectionDataAlt = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionDataAlt);
    },
  },
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step2Data = getStep2SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(2),
        gaName: summaryDataProvider.getGaStepName(2),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step2Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (_, getState) => {
      const state = getState();
      const { name, tables } = RAIL_CONFIGURATOR_STEP_DATA[1].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step3Data = getStep3SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(3),
        gaName: summaryDataProvider.getGaStepName(3),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step3Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (_, getState) => {
      const state = getState();
      const { name, tables } = RAIL_CONFIGURATOR_STEP_DATA[2].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
  {
    prepareData: (state, language: string) => {
      const summaryDataProvider = makeSummaryDataProvider(state, language);
      const step4Data = getStep4SummaryData(summaryDataProvider) ?? [];

      return {
        name: summaryDataProvider.getGaStepName(4),
        gaName: summaryDataProvider.getGaStepName(4),
        tables: [
          {
            name: "",
            gaName: "",
            entries: step4Data.map(transformSummaryDataToV2),
          },
        ],
      };
    },
    trackCompletedStepAction: async (dispatch, getState) => {
      const state = getState();
      const { name, tables } = RAIL_CONFIGURATOR_STEP_DATA[3].prepareData(state, "en");
      const selectionData = transformToSelectionData(tables);

      sendStepCompletedEvent(name, selectionData);
    },
  },
];

export const RAIL_DEFINITION: ConfiguratorPageDefinition = {
  type: ConfiguratorType.Rail,
  productCode: RAIL_CONFIGURATOR_PRODUCT_CODE,

  translationKeys: {
    pageTitle: "configurator.rail.name",
  },
  apiCalls: {
    update: sendRailUpdate,
    reset: sendRailResetConfigurator,
    stepChange: sendRailStepChange,
    stepReset: sendRailDataReset,
    addToCart: sendRailAddToCart,
  },
  summary: RailSummaryData,
  isAddToCartEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    const kmat = getMainKmat({ kmats: state.linearConfigurator.kmats });
    const hasLengthSet = csticHasValue(kmat, RailCstic.GuidewayLength);
    return step >= RAIL_MIN_STEP_TO_BUY && hasLengthSet;
  },
  isSaveEnabled: (state: RootState) => {
    const step = getHighestVisitedStep(state);
    return step >= RAIL_MIN_STEP_TO_SAVE;
  },
  stepHardcodedData: RAIL_CONFIGURATOR_STEP_DATA,
  isConflictAvoidable: (csticName) => {
    if ([RailCstic.GuidewayLength, RailCstic.AL, RailCstic.AR].includes(csticName)) {
      return true;
    }
    if (isGuidewayPieceSizeCsticName(csticName)) {
      return true;
    }
    return false;
  },
  generateDesignation: (kmats) => {
    const kmat = getMainKmat({ kmats });

    const [series, sizeCode, version, guidewayDesign] = getCsticsValue(
      kmat,
      RailCstic.DesignType_Series,
      RailCstic.DesignType_SizeCode,
      RailCstic.DesignType_Version,
      RailCstic.DesignType_GuidewayDesign,
    );

    const join = (separator: string, ...values: string[]) => values.filter(isNonNull).join(separator);

    return join("-", join("", series, sizeCode), version, guidewayDesign) || BASE_DESIGNATION_SEPARATOR;
  },
  gaConfiguratorName: "rail",
  modelAndUpdatePreprocessor: (model) => {
    const kmat = getMainKmat({ kmats: model.kmats });
    const cstics = kmat.cstics;
    const namingHelper = new CsticNamingHelper(cstics);

    namingHelper.updateNames(RailCstic.AL, {
      label: "configurator.rail.section.al.name",
    });
    const alCstic = cstics[RailCstic.AL];
    if (alCstic && alCstic.value) {
      alCstic.value = (+alCstic.value).toFixed(2);
    }

    namingHelper.updateNames(RailCstic.AR, {
      label: "configurator.rail.section.ar.name",
    });
    const arCstic = cstics[RailCstic.AR];
    if (arCstic && arCstic.value) {
      arCstic.value = (+arCstic.value).toFixed(2);
    }

    namingHelper.updateNames(RailCstic.PartSizing, {
      label: "configurator.rail.cstic.partSizing.name",
      value: {
        label: (value) => `configurator.rail.cstic.partSizing.${value}.name`,
      },
    });
    const partSizingCstic = cstics[RailCstic.PartSizing];
    const manualSizingSelected = !!model.customSegmentLengthSelected;
    if (partSizingCstic) {
      // Remove "N" value"
      partSizingCstic.values = partSizingCstic.values.filter((cVal) => cVal.key !== "N");

      if (!manualSizingSelected) {
        // Logic defined in SCHAEFF-19247
        // If the sizing is not set manually - don't show whether it's A or J
        partSizingCstic.value = undefined;
      }
    }

    namingHelper.updateNames(RailCstic.PartAmount, {
      label: "configurator.rail.cstic.partAmount.name",
      value: {
        label: (value) => `configurator.rail.cstic.partAmount.${value}.name`,
      },
    });
    const partAmountCstic = cstics[RailCstic.PartAmount];
    if (partAmountCstic) {
      const pieceLimits = Object.values(model.guidewayPieceLimits)[0];
      if (manualSizingSelected) {
        const { min, max } = pieceLimits;
        partAmountCstic.values = partAmountCstic.values.slice(Math.max(min - 1, 0), max);
      }
    }

    namingHelper.updateNames(RailCstic.LocatingFace, {
      label: "configurator.rail.cstic.locatingFace.name",
      value: {
        label: (value) => `configurator.rail.cstic.locatingFace.${value}.name`,
      },
    });

    namingHelper.updateNames(RailCstic.GuidewayLength, {
      label: "configurator.rail.cstic.guidewayLength.name",
    });

    namingHelper.updateNames(RailCstic.PartOneLength, {
      label: instance.t("page.linear.guidewaySet.pieceLength.title", {
        index: 1,
      }),
    });
    namingHelper.updateNames(RailCstic.PartTwoLength, {
      label: instance.t("page.linear.guidewaySet.pieceLength.title", {
        index: 2,
      }),
    });

    namingHelper.updateNames(RailCstic.PartThreeLength, {
      label: instance.t("page.linear.guidewaySet.pieceLength.title", {
        index: 3,
      }),
    });

    namingHelper.updateNames(RailCstic.PartFourLength, {
      label: instance.t("page.linear.guidewaySet.pieceLength.title", {
        index: 4,
      }),
    });

    namingHelper.updateNames(RailCstic.PartFiveLength, {
      label: instance.t("page.linear.guidewaySet.pieceLength.title", {
        index: 5,
      }),
    });

    const mountingSubstep = model.substeps[SubstepType.Mounting];
    if (mountingSubstep) {
      const DESIGN_VALUES_FOR_DISABLED_MOUNTING_UNDER = ["TKSD"];
      const SIZE_VALUES_FOR_DISABLED_MOUNTING_UNDER = ["30", "35", "45"];

      const designTypeValue = kmat.cstics[RailCstic.Series]?.value ?? "";
      const sizeValue = kmat.cstics[RailCstic.Size]?.value ?? "";

      const clearCsticsToEnable =
        !DESIGN_VALUES_FOR_DISABLED_MOUNTING_UNDER.includes(designTypeValue) ||
        !SIZE_VALUES_FOR_DISABLED_MOUNTING_UNDER.includes(sizeValue);

      const underTile = mountingSubstep.tiles?.find((tile) => tile.cstics[RailCstic.MountingHoleType] === "U");
      if (underTile && clearCsticsToEnable) {
        underTile.csticsToEnable = {};
      }
    }

    if (model.minLength) {
      cstics[HelperCstics.totalLengthMin] = {
        ...EMPTY_FAKE_CSTIC,
        key: HelperCstics.totalLengthMin,
        name: HelperCstics.totalLengthMin,
        value: model.minLength.toString(),
      };
    }
    if (model.maxLength) {
      cstics[HelperCstics.totalLengthMax] = {
        ...EMPTY_FAKE_CSTIC,
        key: HelperCstics.totalLengthMax,
        name: HelperCstics.totalLengthMax,
        value: model.maxLength.toString(),
      };
    }

    return model;
  },
} as const;

const EMPTY_FAKE_CSTIC: LinearCstic = {
  key: "",
  name: "",
  label: "",
  value: "",
  groupId: "",
  values: [],
  type: "",
  info: "",
  description: "",
  readonly: false,
  fake: true,
};

const sendStepCompletedEvent = makeSendStepCompletedEvent(RAIL_DEFINITION);
